import { useState, useEffect } from 'react';
import moment from 'moment';

import { InboxOutlined, PaperClipOutlined, DeleteFilled, DeleteOutlined } from '@ant-design/icons';
import { Form, Modal, message, Divider, Typography, Input, Select, Checkbox, Button, Radio } from 'antd';
import Dragger from 'antd/lib/upload/Dragger';

import { formatDate, formatZipCode } from 'utils/helpers';
import { ErrorResponse } from 'utils/types';
import { ORGANIZATION_NAME } from 'utils/objects';

import FloatLabel from 'components/Common/FloatLabel';
import DatePickerLocalised from 'components/Common/DatePickerLocalised';

import UploadDocumentModalProps, {DocumentCategory, DocumentCategoryItem, MemberDocument} from './Proptypes';
import { UploadFile } from 'antd/lib/upload/interface';

const { Title } = Typography;
const { Option } = Select;

const UploadDocumentModal = (props: UploadDocumentModalProps) => {
  const initialDocumentList = [
    {
      categoryDescription: '',
      category: '',
      tags: '',
      score: '',
      dos: '',
      fractureDate: '',
      positiveNegative: '',
      measureCode: '',
      measureGroup: '',
      measureGroupItem: '',
      measureYear: moment().year().toString(),
      memberFile: [],
      showScore: false,
      showDos: false,
      showFractureDate: false,
      showPositiveNegative: false
    }
  ]
  const types = ['.tif', '.tiff', '.jpg', '.jpeg', '.pdf', '.doc', '.docx','.TIF','.TIFF','.JPG','.JPEG','.PDF','.DOC','.DOCX'];
  const [form] = Form.useForm();

  const [categoryList, setCategoryList] = useState<DocumentCategoryItem[]>([]);
  const [documentList, setDocumentList] = useState<MemberDocument[]>(initialDocumentList);
  const [showMore, setShowMore] = useState(false);
  const [agreementCheckbox, setAgreementCheckbox] = useState('N');
  const [disableSave, setDisableSave] = useState(false);

  useEffect(() => {
    if (props.isUploadDocumentModalVisible) {
      setDisableSave(false);
      getCategory();
    }
  },[props.isUploadDocumentModalVisible]);

  // customRequest to just return a success to attach file to the form instead being actually uploading yet
  const customRequest = ({ onSuccess }: any) => {
    setTimeout(() => onSuccess('Success'), 0);
  };

  const handleRemove = (index: number, fileId: string) => {
    setDocumentList((previous: MemberDocument[]) =>
      documentList.map((item: MemberDocument, itemIndex: number) =>
        itemIndex === index
        ? {...item, memberFile: previous[index].memberFile.filter((item: UploadFile) => item.uid !== fileId)}
        : item
      )
    );
  };

  const beforeUpload = (file: UploadFile, index: number, allowMultipleAttachments: boolean) => {
    const ext = file.name.substring(file.name.lastIndexOf('.'));
    if (!types.includes(ext)) {
      message.error(`You can only upload files of type ${types.join(', ')}`);
    } else {
      // Only allow 1 attachment
      if (documentList[0]?.memberFile.length === 1 && !allowMultipleAttachments) return;

      // Adds file to document list.
      setDocumentList((previous: MemberDocument[]) =>
        documentList.map((item: MemberDocument, itemIndex: number) =>
          itemIndex === index
          ? { ...item, memberFile: [...previous[index].memberFile, file] }
          : item
        )
      );
    }
    return false;
  };

  const showHideShowMore = () => {
    setShowMore(!showMore);
  };

  const getCategory = () => {
    const getCategoryParams = {
      "CompanyId": props.eligibilityMemberInfo.RmiCompanyId,
      "MembMpiNo": props.eligibilityMemberInfo.RmiMembMpiNo
    };

    return props.getCategory(getCategoryParams)
      .then((data: DocumentCategoryItem[]) => {
        setCategoryList(data);
      })
      .catch((err: ErrorResponse) => {
        message.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const onSubmit = async () => {
    form.validateFields().then(_ => {
      submitForm();
    });
  };

  const submitForm = async () => {
    await Promise.all(documentList.map(async (currentValue: MemberDocument) => {
      const createUploadFileData = new FormData();
      createUploadFileData.append("companyId", props.eligibilityMemberInfo.RmiCompanyId);
      createUploadFileData.append("membMpiNo", props.eligibilityMemberInfo.RmiMembMpiNo);
      createUploadFileData.append("membKeyId", props.eligibilityMemberInfo.RmiMembKeyId);
      createUploadFileData.append("membProvKeyId", props.eligibilityMemberInfo.RmiProvKeyId);
      createUploadFileData.append("documentCategory", currentValue.category);
      createUploadFileData.append("documentTag", currentValue.tags);
      createUploadFileData.append("dos", currentValue.dos);
      createUploadFileData.append("fractureDate", currentValue.fractureDate);
      createUploadFileData.append("positive", currentValue.positiveNegative);
      createUploadFileData.append("score", currentValue.score);
      createUploadFileData.append("tncAgreement", agreementCheckbox);
      createUploadFileData.append("userId", sessionStorage.user_id);
      createUploadFileData.append("pacNoteDoc", 'false');
      createUploadFileData.append("noteId", '0');
      createUploadFileData.append("measureYear", currentValue.measureYear);
      currentValue.memberFile.forEach((fileAttachment: any) => {
        createUploadFileData.append("memberFile[]", fileAttachment);
      })
      if (currentValue.measureCode) createUploadFileData.append("measureCode", currentValue.measureCode);
      if (currentValue.measureGroup) createUploadFileData.append("measureGroup", currentValue.measureGroup);
      if (currentValue.measureGroupItem) createUploadFileData.append("measureGroupItem", currentValue.measureGroupItem);

      return props.uploadFilestoAuth(createUploadFileData)
        .then((response: number[]) => {
          message.success("Document successfully uploaded.");
          return response;
        })
        .catch((err: ErrorResponse) => {
          message.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
        });
    }))
      .then((documentId: number[][]) => {
      props.isUploadDocumentModalVisibleHandler('submit', documentId);
    })
    .finally(() => {
      setDocumentList(initialDocumentList);
      form.resetFields();
    });
  };

  const onCancel = () => {
    setDocumentList(initialDocumentList);
    setDisableSave(false);
    form.resetFields();
    props.isUploadDocumentModalVisibleHandler('close', []);
  };

  //add category button
  const addAnotherCategory = () => {
    setDocumentList((previousDocuments: MemberDocument[]) => [...previousDocuments, ...initialDocumentList]);
  };

  //the orange x button to remove the a document list
  const removeCategory = () => {
    if (documentList.length > 1) setDocumentList(documentList.slice(0, -1));
  };

  //updates individual property of the array of object list in each category
  const updateDocumentList = (e: any, index: number, property: DocumentCategory) => {
    const updatedDocumentList = documentList.map((item: MemberDocument, itemIndex: number) => {
      if (itemIndex === index) {
        if (property === DocumentCategory.POSITIVE_NEGATIVE) return {...item, positiveNegative: e.target.value};
        else if (property === DocumentCategory.DOS) return {...item, dos: e};
        else if (property === DocumentCategory.FRACTURE_DATE) return {...item, fractureDate: e};
        else if (property === DocumentCategory.MEASURE_YEAR) return {...item, measureYear: e.toString()};
        else if (property === DocumentCategory.TAGS) return {...item, tags: e.target.value};
        else if (property === DocumentCategory.SCORE) return {...item, score: e.target.value};
        else if (property === DocumentCategory.CATEGORY) {
          const category = JSON.parse(e);
          form.validateFields(['categoryDescription']);
          return {...item,
            categoryDescription: category.RmiCategoryDesc,
            category: category.RmiCategoryCode,
            measureGroup: category.RmiMeasureGroup,
            measureCode: category.RmiMeasureCode,
            showScore: category.RmiShowScore,
            showDos: category.RmiShowDos,
            showFractureDate: category.RmiShowFractureDate,
            showPositiveNegative: category.RmiShowPositiveNegative
          };
        }
      }
      return item;
    });

    setDocumentList(updatedDocumentList);
  };

  return (
    <>
      <Modal
        visible={props.isUploadDocumentModalVisible}
        width={1000}
        maskClosable={false}
        onCancel={onCancel}
        footer={[
          <Button
            key="cancel"
            onClick={onCancel}
          >
            Close
          </Button>,
          <Button
            key="submit"
            type="primary"
            form="attachmentForm"
            onClick={onSubmit}
            loading={props.isUploading}
            disabled={disableSave}
          >
            Save
          </Button>,
        ]}
      >
        <Title className="text-center" level={3}>Document Upload</Title>

        <Divider/>

        <div className=" row flex-lg-wrap justify-content-start mb-4 ml-0">

          <div className="col-lg-12 col-md-12">
            <table className="test"  style={{ textAlign: "left" }}>
              <tbody>
                <tr>
                  <th className="bold">Patient Name:</th>
                  <td>{props.eligibilityMemberInfo.RmiRevFullName}</td>
                </tr>
                <tr>
                  <th className="bold">Address:</th>
                  <td>{props.eligibilityMemberInfo.RmiStreet}</td>
                </tr>
                <tr>
                  <th className="bold">City, State, Zip:</th>
                  <td>{props.eligibilityMemberInfo.RmiCity}{props.eligibilityMemberInfo.RmiStreet ? ',': ''} {props.eligibilityMemberInfo.RmiState} {formatZipCode(props.eligibilityMemberInfo.RmiZip)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="col-lg-12 col-md-12">
            <table className="test"  style={{ textAlign: "left" }}>
              <tbody>
                <tr>
                  <th className="bold">DOB:</th>
                  <td>{formatDate(props.eligibilityMemberInfo.RmiBirth, 'MM/DD/YYYY')}</td>
                  <th className="bold">Gender:</th>
                  <td>{props.eligibilityMemberInfo.RmiSex}</td>
                </tr>
                <tr>
                  <th className="bold">Member ID:</th>
                  <td>{props.eligibilityMemberInfo.RmiMembId}</td>
                </tr>
                <tr>
                  <th className="bold">Health Plan:</th>
                  <td>{props.eligibilityMemberInfo.RmiHpName}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <Divider />

        <Form
          form={form}
          name={'documentCategoryForm'}
          onFinish={onSubmit}
          initialValues={{
            measureYear: moment().year().toString()
          }}
        >
          <div className="row">
            <div className="col-lg-24 col-md-24">
              <p>
                <b>Add Attachment &nbsp; &nbsp; (Only files of type .tif, .tiff, .jpg, .jpeg, .pdf, .doc, .docx,.TIF, .TIFF, .JPG, .JPEG, .PDF, .DOC, .DOCX will be accepted)</b>
              </p>
            </div>
          </div>

          {documentList.map((option: MemberDocument, index: number) => {
            return <>
              <div className="row">
                <div className="col-lg-12 col-md-12">
                  <Form.Item>
                    <FloatLabel
                      label=""
                      name="categoryDescription"
                      form={form}
                      rules={[
                        () => ({
                          async validator() {
                            if (option.categoryDescription) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('Required'));
                          },
                        })
                      ]}
                    >
                      <span>Category </span><div style={{color:'red', display: 'inline-block'}}>(*)</div>: &nbsp;
                      <Select
                        showSearch
                        value={option.categoryDescription}
                        style={{width: '80%'}}
                        onChange={value => updateDocumentList(value, index, DocumentCategory.CATEGORY)}
                      >
                        {categoryList.map((option: DocumentCategoryItem, key: number) => {
                          return <Option key={key} value={JSON.stringify(option)}>{option.RmiCategoryDesc}</Option>;
                        })}
                      </Select>
                    </FloatLabel>
                  </Form.Item>

                  <Form.Item>
                    <span>Tags: </span>
                    <Input
                      style={{width: '90%'}}
                      value={option.tags}
                      onChange={e => updateDocumentList(e, index, DocumentCategory.TAGS)}
                    />
                  </Form.Item>

                  <Form.Item>
                    <span>Measure Year: </span>
                    <Select
                      style={{width: '60%'}}
                      defaultValue={moment().year().toString()}
                      value={option.measureYear}
                      placeholder="Select a status"
                      onChange={value => updateDocumentList(value, index, DocumentCategory.MEASURE_YEAR)}
                    >
                      <Option
                        value={moment().year()}
                      >
                        {moment().year()}
                      </Option>
                      <Option
                        value={moment().year() - 1}
                      >
                        {moment().year() - 1}
                      </Option>
                    </Select>
                  </Form.Item>
                </div>

                <div className="col-lg-12 col-md-12">
                  <Form.Item>
                    <Dragger
                      fileList={option.memberFile}
                      customRequest={customRequest}
                      beforeUpload={(file) => beforeUpload(file, index, props.allowMultipleAttachments)}
                      multiple={props.allowMultipleAttachments}
                      showUploadList={false}
                      accept={types.join(',')}
                    >
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                      </p>
                      <p className="ant-upload-text">Click or drag a file at a time to this area to upload</p>
                    </Dragger>

                    <div className="col-lg-24 mt-3 mb-3 pl-0">
                      {option.memberFile.map((file: UploadFile, fileIndex: number) => (
                        <div key={fileIndex}>
                          <span>
                            <PaperClipOutlined className="mr-2" />
                          </span>
                          {file.name}
                          <span>
                            <DeleteFilled className="ml-1" onClick={() => handleRemove(index, file.uid)} />
                          </span>
                        </div>
                      ))}
                    </div>
                  </Form.Item>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-7 col-md-7">
                  { option.showDos && (
                    <Form.Item>
                      <span>DOS: </span>
                      <FloatLabel
                        label=""
                        name="dos"
                        form={form}
                        rules={[
                          () => ({
                            async validator() {
                              if (props.claimDos && moment(props.claimDos).year() >= 1900 && props.claimDos !== 'Invalid date') {
                                return Promise.resolve();
                              }
                              return Promise.reject(new Error('Required'));
                            },
                          })
                        ]}
                        required
                      >
                        <DatePickerLocalised
                          date={option.dos}
                          setDate={(date) => { updateDocumentList(date, index, DocumentCategory.DOS); props.setClaimDos(moment(date).format('MM/DD/YYYY')); }}
                          formInstance={form}
                          formItemName={'dos'}
                        />
                      </FloatLabel>
                    </Form.Item>
                  )}
                  { option.showFractureDate && (
                    <Form.Item>
                      <span>Fracture Date: </span>
                      <DatePickerLocalised
                        date={option.fractureDate}
                        setDate={(date) => updateDocumentList(date, index, DocumentCategory.FRACTURE_DATE)}
                        formInstance={form}
                        formItemName={'fractureDate'}
                      />
                    </Form.Item>
                  )}

                  { option.showScore && (
                    <Form.Item>
                      <span>Score: </span>
                      <Input type="number" value={option.score} onChange={(e) => updateDocumentList(e, index, DocumentCategory.SCORE)} />
                    </Form.Item>
                  )}

                  { option.showPositiveNegative && (
                    <Form.Item>
                      <span>Positive Negative</span>
                      <Radio.Group onChange={(e) => updateDocumentList(e, index, DocumentCategory.POSITIVE_NEGATIVE)} value={option.positiveNegative}>
                        <Radio value="True">Positive</Radio>
                        <Radio value="False">Negative</Radio>
                      </Radio.Group>
                    </Form.Item>
                  )}
                </div>
              </div>
              {documentList.length > 1 && index !== documentList.length - 1 && <Divider />}
            </>
          })}

          { props.uploadMultipleCategories ?
            <div className="btn-group">
              <Button type="primary" onClick={addAnotherCategory}>
                Add a category
              </Button>
              {documentList.length > 1 &&
                <Button
                  type="primary"
                  style={{ width: '30px', padding: '0', marginLeft: '5px'}}
                  onClick={() => removeCategory()}
                  danger
                >
                  <DeleteOutlined/>
                </Button>}
            </div>
            : null
          }

          <div className="row mt-5">
            <div className="col">
              <FloatLabel
                label=""
                name="agreementCheckbox"
                form={form}
                rules={[
                  () => ({
                    async validator() {
                      if (agreementCheckbox === 'Y') {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Required'));
                    },
                  })
                ]}
              >
                <Checkbox value={agreementCheckbox} onChange={(e) => setAgreementCheckbox(e.target.checked ? 'Y' : 'N')}>I have reviewed and/or overseen the medical records being attached and confirm to the best of my knowledge this information as follows:</Checkbox>
              </FloatLabel>
              { showMore === true && (
                <>
                  <ul>
                    <li>
                      The attached laboratory report and/or radiology report is genuine and part of this member's medical record history.
                    </li>
                    <li>
                      I have reviewed this lab and/or radiology report as confirmed with a date and initials.
                    </li>
                    <li>
                      The attached consultation report is genuine and part of this member’s medical record.
                    </li>
                    <li>
                      I have reviewed this consultation report as confirmed with a date and initials or referenced in the follow up visit notes.
                    </li>
                  </ul>
                  Please note that {ORGANIZATION_NAME} may request access to the medical records as part of a validation process, if required by the member's Health Plan or Government Agency ( ie. CMS and DHCS).
                </>
              )} { showMore === false &&
                <Button type="link" onClick={() => showHideShowMore()}>Show More</Button>
              } { showMore === true &&
                <Button type="link" onClick={() => showHideShowMore()}>Show Less</Button>
              }
            </div>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default UploadDocumentModal;